/** @jsx jsx */

import _ from 'lodash';
import { $set, update } from 'qim';
import { Component, Fragment } from 'react';

import { singular } from '@zapier/common-utils';
import { jsx } from '@emotion/core';
import { Button, Margin } from '@zapier/design-system';

import Config from 'app/developer-v3/pages/actions/ActionApi/Config';
import ConfigLegacy from 'app/developer-v3/pages/actions/ActionApi/ConfigLegacy';
import CtaCreateZap from 'app/developer-v3/components/CtaCreateZap';
import EditorBodyLayout from 'app/developer-v3/layouts/EditorBodyLayout';
import HelpBoxApiConfig from 'app/developer-v3/components/HelpBoxApiConfig';
import NonFormaticFieldWrapper from 'app/developer-v3/components/NonFormaticFieldWrapper';
import OutputFieldsEditor from 'app/developer-v3/components/OutputFieldsEditor';
import RequestTester from 'app/developer-v3/components/RequestTester';
import SampleOutputEditor from 'app/developer-v3/components/SampleOutputEditor';
import SaveButtonGroup from 'app/developer-v3/components/SaveButtonGroup';
import SetupStep from 'app/developer-v3/components/SetupStep';
import Link from 'app/common/components/Link';
import getFormFields from 'app/developer-v3/pages/actions/ActionApi/formFields';
import {
  BASE_DOC_URL_VB,
  highlightActionApiKeys,
} from 'app/developer-v3/constants';
import {
  createValidator,
  deriveOutputFields,
  getBaseAuthUrl,
  getHighlightActionApiFieldKey,
  getQueryHighlightStep,
  validateRequired,
} from 'app/developer-v3/utils';
import type { ErrorList } from 'app/developer-v3/types/validator';
import type {
  ActionParams,
  ActionSchema,
  ApiHighlightField,
  FormFieldTemplate,
  LegacyActionSchema,
  QueryHighlight,
  TestRequestType,
} from 'app/developer-v3/types';
import type { AuthenticationType } from 'app/developer-v3/platformSchema/authentication';
import type { BasicActionOperationSchema } from 'app/developer-v3/platformSchema/action-operation';
import type {
  AuthFieldSchema,
  InputFieldSchema,
  OutputFieldSchema,
} from 'app/developer-v3/platformSchema/field';
import { RequestSchema } from 'app/developer-v3/platformSchema/request';

type Props = {
  action: ActionSchema;
  actionLegacy?: LegacyActionSchema;
  authFields: Array<AuthFieldSchema>;
  authType: AuthenticationType;
  getSuggestions: (operation: string) => Array<string>;
  isActionSetup: boolean;
  isAuthSetupComplete: boolean;
  isReadOnly: boolean;
  isSaving: boolean;
  params: ActionParams;
  query: QueryHighlight;
  requestTemplate: RequestSchema;
  resetTestLogs: () => void;
  resetTestRequest: () => void;
  saveApiChanges: (
    action: ActionSchema,
    actionLegacy?: LegacyActionSchema
  ) => void;
  selectedApi: string;
  setUnsavedChanges: (hasUnsavedChanges: boolean) => void;
  testRequest: TestRequestType;
};

type State = {
  action: ActionSchema;
  actionLegacy?: LegacyActionSchema;
  activeStep: number;
  errors: ErrorList;
  hasAttemptedSave: boolean;
  highlightField: ApiHighlightField;
  isFormValid: boolean;
  isSampleValid: boolean;
  outputFieldsEditorKey: number;
  sampleText: string;
};

class ActionApi extends Component<Props, State> {
  // TODO: Figure out why this component is slow on first render
  // Users can type before refreshValidation is called setting this function
  validateForm: (values: { perform: string }) => ErrorList = () => ({});

  getQueryStep(query: QueryHighlight = { highlight: '' }) {
    const { highlight } = query;

    const queryToStepsMap = {
      perform: 1,
      sample: 3,
      outputFields: 3,
    };

    return getQueryHighlightStep(queryToStepsMap, highlight, 1);
  }

  getFieldToHighlight(
    query: QueryHighlight = { highlight: '', type: '', message: '' }
  ) {
    const { highlight = '', type = '', message = '' } = query;

    return {
      field: getHighlightActionApiFieldKey(highlight),
      type,
      message,
    };
  }

  state = {
    action: this.props.action,
    actionLegacy: this.props.actionLegacy,
    activeStep: this.getQueryStep(this.props.query),
    highlightField: this.getFieldToHighlight(this.props.query),
    errors: {},
    hasAttemptedSave: false,
    isSampleValid: true,
    isFormValid: !!this.props.action?.operation,
    outputFieldsEditorKey: 0,
    sampleText: JSON.stringify(
      _.get(this.props.action, ['operation', 'sample'], {}),
      null,
      2
    ),
  };

  componentDidMount() {
    this.props.resetTestLogs();
    this.props.resetTestRequest();
  }

  componentDidUpdate(prevProps: Props) {
    const fields = getFormFields(this.props.params.actionType);
    this.refreshValidator(fields);

    if (
      this.props.query &&
      this.props.query.highlight !== prevProps.query.highlight
    ) {
      this.setState({
        highlightField: this.getFieldToHighlight(this.props.query),
      });
    }
  }

  get isSaveDisabled() {
    return this.props.isReadOnly || !this.state.isSampleValid
      ? true
      : this.state.hasAttemptedSave
      ? !this.state.isFormValid
      : false;
  }

  get activeErrors() {
    const { hasAttemptedSave, errors } = this.state;
    return hasAttemptedSave ? errors : {};
  }

  get needsAuthMessage() {
    return (
      <span>
        Finish{' '}
        <Link
          href={`${getBaseAuthUrl(
            this.props.params.id,
            this.props.params.version
          )}/edit`}
        >
          configuring authentication
        </Link>{' '}
        so you can test your Action
      </span>
    );
  }

  get configHelpText() {
    return {
      heading: 'Legacy Web Builder API Request',
      description: <HelpBoxApiConfig params={this.props.params} />,
    };
  }

  get operationDefaults() {
    return {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    };
  }

  get validPerformObject() {
    const perform = this.state.action.operation?.perform || {};

    if (typeof perform === 'string') {
      return perform;
    }

    if ('source' in perform) {
      return { source: perform.source };
    }

    if ('url' in perform) {
      const { source, url, ...rest } = perform;
      return url ? { url, ...rest } : { ...this.operationDefaults };
    }

    return { ...this.operationDefaults, ...perform };
  }

  get configFormValues() {
    const values = { perform: this.validPerformObject };

    return values;
  }

  get configFormValuesLegacy() {
    return _.get(this.state, ['actionLegacy', 'operation'], {});
  }

  get config() {
    return this.state.actionLegacy ? (
      <ConfigLegacy
        isDisabled={this.props.isReadOnly}
        onChange={this.onChangeOperationLegacy}
        type={this.props.params.actionType}
        value={this.configFormValuesLegacy}
      />
    ) : (
      <Config
        isDisabled={this.props.isReadOnly}
        meta={{
          getSuggestions: this.props.getSuggestions,
          errors: this.activeErrors,
          info:
            this.state.highlightField.field ===
              highlightActionApiKeys.perform && this.state.highlightField,
        }}
        onChange={this.onFormChange}
        type={this.props.params.actionType}
        value={this.configFormValues}
      />
    );
  }

  get requestsForTesting() {
    return [
      {
        label: 'Perform',
        operationMethod: 'perform',
        request: this.configFormValues.perform,
      },
    ];
  }

  refreshValidator = (fields: Array<FormFieldTemplate>) => {
    this.validateForm = createValidator(fields, [validateRequired]);
  };

  validatePerform = (operation: BasicActionOperationSchema) => {
    const { url, source } = operation.perform;
    const errors = this.validateForm({ perform: url || source });
    const isFormValid = _.isEmpty(errors);

    this.setState({ isFormValid, errors });

    return isFormValid;
  };

  setUnsavedChanges = () => this.props.setUnsavedChanges(true);
  unsetUnsavedChanges = () => this.props.setUnsavedChanges(false);

  saveAction = (action: ActionSchema, actionLegacy?: LegacyActionSchema) => {
    this.props.saveApiChanges(action, actionLegacy);
    this.unsetUnsavedChanges();
  };

  onChangeOperationLegacy = (value?: {} | Array<InputFieldSchema>) => {
    this.setState(
      {
        actionLegacy: update(
          ['operation', $set(value)],
          this.state.actionLegacy
        ),
      },
      this.setUnsavedChanges
    );
  };

  onChangeOperation = (
    operationKey: string,
    requestDefinition?: {} | Array<InputFieldSchema>
  ) => {
    const action = update(
      ['operation', operationKey, $set(requestDefinition)],
      this.state.action
    );

    if (!_.isEqual(action, this.state.action)) {
      this.validatePerform(action.operation);
      this.setState({ action }, this.setUnsavedChanges);
    }
  };

  onUpdateSample = (
    value: string,
    _name: string,
    didConfirm: boolean,
    isValid?: boolean
  ) => {
    if (didConfirm) {
      this.setState({ isSampleValid: isValid });
      try {
        const jsonValue = JSON.parse(value);
        this.onChangeOperation(
          'sample',
          _.isEmpty(jsonValue) ? undefined : jsonValue
        );
        this.setState({ isSampleValid: true });
      } catch (e) {
        // Ignore as people might be mid-typing
      }
    }

    this.setState({ sampleText: value }, this.setUnsavedChanges);
  };

  onGenerateOutputClick = () => {
    const { outputFieldsEditorKey, action } = this.state;
    const { outputFields, sample } = action.operation;
    this.onChangeOperation(
      'outputFields',
      deriveOutputFields({ outputFields, sample })
    );
    this.setState({
      outputFieldsEditorKey: outputFieldsEditorKey + 1,
    });
  };

  onUpdateOutputFields = (outputFields: Array<OutputFieldSchema>) => {
    this.onChangeOperation('outputFields', outputFields);
  };

  onIncrementActiveStep = () => {
    this.setState(prevState => ({
      activeStep: prevState.activeStep + 1,
    }));
  };

  onSaveAndIncrementActiveStep = () => {
    const { action, actionLegacy } = this.state;

    if (this.validatePerform(action.operation)) {
      this.saveAction(action, ...(actionLegacy ? [actionLegacy] : []));

      this.onIncrementActiveStep();
    }

    this.setState({ hasAttemptedSave: true });
  };

  onFormChange = (value: {}, name: string) => {
    this.onChangeOperation(name, value);
  };

  render() {
    const {
      isAuthSetupComplete,
      isReadOnly,
      isSaving,
      params,
      testRequest,
    } = this.props;
    const {
      action: { operation = {} },
      actionLegacy,
      activeStep,
      outputFieldsEditorKey,
      sampleText,
      highlightField,
    } = this.state;

    return (
      <EditorBodyLayout isDisabled={this.isSaveDisabled} isSaving={isSaving}>
        {this.props.isActionSetup && (
          <Margin margin="0px 0px 40px 0px">
            <CtaCreateZap type="action" />
          </Margin>
        )}
        <SetupStep
          helpText={actionLegacy && this.configHelpText}
          isInitiallyOpen={activeStep === 1}
          stepLabel="Step 1"
          title="Configure your API Request"
        >
          {this.config}
          <Margin margin="5px 0px 5px 0px">
            <SaveButtonGroup
              isDisabled={this.isSaveDisabled}
              isSaving={isSaving}
              onSave={this.onSaveAndIncrementActiveStep}
              saveText="Save API Request &amp; Continue"
            />
          </Margin>
        </SetupStep>
        <SetupStep
          isInitiallyOpen={activeStep === 2}
          stepLabel="Step 2"
          title="Test your API Request"
        >
          <NonFormaticFieldWrapper
            title={
              (this.requestsForTesting.length > 1 && 'Select a Request') || ''
            }
          >
            <RequestTester
              appId={params.id}
              canConnectAuth={isAuthSetupComplete}
              inputFields={operation.inputFields}
              needsAuthMessage={this.needsAuthMessage}
              objectKey={params.actionKey}
              requests={this.requestsForTesting}
              testType={singular(params.actionType)}
              versionNumber={params.version}
            />
          </NonFormaticFieldWrapper>
          <Margin margin="-20px 0px 5px 0px">
            <SaveButtonGroup
              isDisabled={this.isSaveDisabled}
              isSaving={isSaving}
              onSave={this.onIncrementActiveStep}
              saveText="Finish Testing &amp; Continue"
            />
          </Margin>
        </SetupStep>
        <SetupStep
          isInitiallyOpen={activeStep === 3}
          stepLabel="Step 3"
          title="Define your Output"
        >
          <NonFormaticFieldWrapper
            helpText={`Enter JSON-formatted sample output data for the most commonly used fields from this Action to use in Zapier as sample data if users don’t test it. <a href="${BASE_DOC_URL_VB}/build/sample-data#sample-data" target="_blank">Learn more</a>.`}
            title="Sample Data"
          >
            {({ fieldLabel }) => (
              <Fragment>
                {fieldLabel}
                <SampleOutputEditor
                  meta={
                    highlightField.field === highlightActionApiKeys.sample && {
                      info: highlightField,
                    }
                  }
                  onChange={this.onUpdateSample}
                  testRequestOutput={testRequest.output}
                  value={sampleText}
                />
              </Fragment>
            )}
          </NonFormaticFieldWrapper>
          <Margin margin="0px 0px 30px 0px">
            <Button
              isDisabled={!operation.sample || isReadOnly}
              onPress={this.onGenerateOutputClick}
              variant="secondary"
            >
              Generate Output Field Definitions
            </Button>
          </Margin>
          <NonFormaticFieldWrapper
            helpText={`Add user friendly names for the most popular fields from API output data. <a href="${BASE_DOC_URL_VB}/build/sample-data#output-fields" target="_blank">Learn more</a>.`}
            title="Output Fields"
          >
            <OutputFieldsEditor
              initialItems={operation.outputFields || []}
              isDisabled={isReadOnly}
              key={outputFieldsEditorKey}
              meta={
                highlightField.field ===
                  highlightActionApiKeys.outputFields && {
                  info: highlightField,
                }
              }
              onUpdate={this.onUpdateOutputFields}
            />
          </NonFormaticFieldWrapper>
          <Margin margin="-20px 0px 5px 0px">
            <SaveButtonGroup
              isDisabled={this.isSaveDisabled}
              isSaving={isSaving}
              onSave={this.onSaveAndIncrementActiveStep}
              saveText="Save Output &amp; Finish"
            />
          </Margin>
        </SetupStep>
      </EditorBodyLayout>
    );
  }
}

export default ActionApi;
